// src/components/Doctor/DoctorVideoCall.jsx
import React, { useEffect, useRef, useState, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import {
  startCall,
  endCall,
  sendOffer,
  sendIceCandidate,
  fetchMessages,
} from "../../Redux/Slice/Doctor/callSlice";
import { Button, Input, Spin, Typography, Divider, Card } from "antd";
import { toast } from "react-toastify";
import { saveDiagnosis } from "../../Redux/Slice/Doctor/Slots";
import SummaryModal from "./SummaryModal";
import { Collapse } from "antd";

const { Panel } = Collapse;

const { Title, Text } = Typography;

const DoctorVideoCall = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { patientId, appointmentId, patientName } = location.state || {};
  const user = JSON.parse(localStorage.getItem("user"));
  const userId = user?.userId;

  const localVideoRef = useRef(null);
  const remoteVideoRef = useRef(null);
  const pcRef = useRef(null);
  const pendingCandidates = useRef([]);
  const localStreamRef = useRef(null);
  const pollIntervalRef = useRef(null);

  const [roomCode, setRoomCode] = useState(null);
  const [iceServers, setIceServers] = useState([]);
  const [isVideoOn, setIsVideoOn] = useState(true);
  const [isAudioOn, setIsAudioOn] = useState(true);
  const [loading, setLoading] = useState(true);

  const [chiefCompliant, setChiefCompliant] = useState("");
  const [diagnosis, setDiagnosis] = useState("");
  const [notes, setNotes] = useState("");
  const [managementPlan, setManagementPlan] = useState("");
  const [allergy, setAllergy] = useState("");
  const [medication, setMedication] = useState("");
  const [surgeryHistory, setSurgeryHistory] = useState("");
  const [summary, setSummary] = useState(null);

  const [isSummaryModalOpen, setIsSummaryModalOpen] = useState(false);
  const [callStartTime, setCallStartTime] = useState(null);
  const [callDuration, setCallDuration] = useState("00:00");

  // const [openFields, setOpenFields] = useState({});

  // const toggleField = (field) => {
  //   setOpenFields((prev) => ({
  //     ...prev,
  //     [field]: !prev[field],
  //   }));
  // };

  // Call Timer
  useEffect(() => {
    if (!callStartTime) return;
    const interval = setInterval(() => {
      const elapsed = Math.floor((Date.now() - callStartTime) / 1000);
      const minutes = String(Math.floor(elapsed / 60)).padStart(2, "0");
      const seconds = String(elapsed % 60).padStart(2, "0");
      setCallDuration(`${minutes}:${seconds}`);
    }, 1000);
    return () => clearInterval(interval);
  }, [callStartTime]);

  // Prevent screenshots (optional)
  useEffect(() => {
    const handler = (e) => {
      if (e.key === "PrintScreen") {
        e.preventDefault();
        toast.warning("Screenshots are disabled for security reasons.");
      }
    };
    document.addEventListener("keyup", handler);
    return () => document.removeEventListener("keyup", handler);
  }, []);

  const handleSaveSummary = () => {
    const payload = {
      appointmentId,
      chiefCompliant,
      diagnosis,
      notes,
      managementPlan,
      allergy,
      medication,
      surgeryHistory,
    };

    dispatch(saveDiagnosis(payload))
      .unwrap()
      .then((res) => {
        toast.success("Summary saved");
        setSummary(res || {});
        setIsSummaryModalOpen(true);
      })
      .catch(() => {
        toast.error("Failed to save summary");
      });
  };

  const createPeerConnection = useCallback(
    (iceServersList) => {
      const pc = new RTCPeerConnection({ iceServers: iceServersList });

      pc.ontrack = (event) => {
        if (remoteVideoRef.current && event.streams[0]) {
          remoteVideoRef.current.srcObject = event.streams[0];
          remoteVideoRef.current
            .play()
            .catch(() => console.warn("Remote video play failed"));
        }
      };

      pc.onicecandidate = (event) => {
        if (event.candidate && roomCode && pc.signalingState !== "closed") {
          console.log("Candidate type:", event.candidate.type);
          dispatch(
            sendIceCandidate({
              roomCode,
              candidate: event.candidate?.candidate,
              sdpMid: event.candidate?.sdpMid,
              sdpMLineIndex: event.candidate?.sdpMLineIndex,
              senderId: userId,
            })
          );
        }
      };

      return pc;
    },
    [dispatch, roomCode, userId]
  );

  // Init call
  useEffect(() => {
    if (!patientId || !appointmentId) {
      toast.error("Missing appointment details");
      navigate("/appointments");
      return;
    }

    const initCall = async () => {
      try {
        setLoading(true);

        const res = await dispatch(
          startCall({ patientId, appointmentId })
        ).unwrap();

        const { roomCode: code, iceServers: servers } = res.data;
        setRoomCode(code);
        setIceServers(servers);

        if (!pcRef.current) {
          if (!servers || !Array.isArray(servers)) {
            console.warn("No ICE servers provided — using Google STUN");
            servers = [{ urls: "stun:stun.l.google.com:19302" }];
          }
          pcRef.current = createPeerConnection(servers);
        }

        if (!pcRef.current) {
          toast.error("Peer connection initialization failed");
          return;
        }

        const stream = await navigator.mediaDevices.getUserMedia({
          video: true,
          audio: true,
        });
        localStreamRef.current = stream;

        if (localVideoRef.current) {
          localVideoRef.current.srcObject = stream;
          localVideoRef.current
            .play()
            .catch(() => console.warn("Local video play failed"));
        }

        stream.getTracks().forEach((track) => {
          if (pcRef.current && pcRef.current.signalingState !== "closed") {
            pcRef.current.addTrack(track, stream);
          }
        });

        let offer;
        try {
          console.log("Creating offer with:", pcRef.current);
          offer = await pcRef.current.createOffer({
            offerToReceiveAudio: true,
            offerToReceiveVideo: true,
          });
          await pcRef.current.setLocalDescription(offer);
        } catch (err) {
          console.error("Failed to create WebRTC offer: " + err.message);
          return;
        }

        dispatch(
          sendOffer({
            roomCode: code,
            sdp: offer.sdp,
            senderId: userId,
          })
        );

        setCallStartTime(Date.now());
        toast.success("Video call started");
      } catch (err) {
        toast.error("Failed to start call: " + err.message);
      } finally {
        setLoading(false);
      }
    };

    initCall();

    return () => {
      // cleanup
      if (pcRef.current) {
        pcRef.current.close();
        pcRef.current = null;
      }
      clearInterval(pollIntervalRef.current);
      localStreamRef.current?.getTracks().forEach((t) => t.stop());
    };
  }, [
    dispatch,
    patientId,
    appointmentId,
    userId,
    navigate,
    createPeerConnection,
  ]);

  // Poll signaling messages
  useEffect(() => {
    if (!roomCode) return;

    pollIntervalRef.current = setInterval(async () => {
      try {
        if (!pcRef.current || pcRef.current.signalingState === "closed") return;

        const msgs = await dispatch(fetchMessages(roomCode)).unwrap();

        if (
          msgs?.answer &&
          pcRef.current &&
          !pcRef.current.currentRemoteDescription
        ) {
          await pcRef.current.setRemoteDescription(
            new RTCSessionDescription(msgs.answer)
          );
          for (const candidate of pendingCandidates.current) {
            await pcRef.current.addIceCandidate(new RTCIceCandidate(candidate));
          }
          pendingCandidates.current = [];
        }

        for (const candidate of msgs.iceCandidates) {
          try {
            if (pcRef.current?.currentRemoteDescription) {
              await pcRef.current.addIceCandidate(
                new RTCIceCandidate(candidate)
              );
            } else {
              console.warn(
                "RemoteDescription not set yet — queuing ICE candidate"
              );
              pendingCandidates.current.push(candidate);
            }
          } catch (err) {
            console.error("Failed to add ICE candidate:", candidate, err);
          }
        }
      } catch (err) {
        console.error("Doctor polling error", err);
      }
    }, 3000);

    return () => clearInterval(pollIntervalRef.current);
  }, [roomCode, dispatch]);

  // End call
  const handleEndCall = async () => {
    try {
      if (roomCode) await dispatch(endCall({ roomCode })).unwrap();
      clearInterval(pollIntervalRef.current);
      if (pcRef.current) {
        pcRef.current.onicecandidate = null;
        pcRef.current.ontrack = null;
        pcRef.current.close();
        pcRef.current = null;
      }
      localStreamRef.current?.getTracks().forEach((t) => t.stop());
      navigate("/appointments");
    } catch {
      toast.error("Failed to end call");
    }
  };

  return (
    <div
      style={{
        height: "100vh",
        display: "flex",
        flexDirection: "column",
        background: "#f9fafc",
      }}
    >
      {/* Header */}
      <div
        style={{
          padding: "15px 30px",
          background: "#002766",
          color: "#fff",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <div>
          <Title level={4} style={{ color: "#fff", margin: 0 }}>
            Doctor Video Call
          </Title>
          <Text style={{ color: "#d9d9d9" }}>
            Room: {roomCode} | Patient: {patientName || patientId}
          </Text>
          <div style={{ marginTop: "5px", fontSize: "13px", color: "#52c41a" }}>
            🔒 This call is secure. Screenshots are restricted.
          </div>
        </div>
        <div>
          <Text strong style={{ fontSize: "18px", color: "#52c41a" }}>
            ⏱ {callDuration}
          </Text>
        </div>
      </div>

      {/* Content */}
      {loading ? (
        <Spin fullscreen />
      ) : (
        <div style={{ flex: 1, display: "flex", padding: "20px", gap: "20px" }}>
          {/* Video Section */}
          <div
            style={{
              flex: 2,
              position: "relative",
              borderRadius: "10px",
              overflow: "hidden",
              background: "black",
              height: "80vh",
            }}
          >
            <video
              ref={remoteVideoRef}
              autoPlay
              playsInline
              width="100%"
              height="100%"
              style={{ objectFit: "cover" }}
            />
            <video
              ref={localVideoRef}
              autoPlay
              playsInline
              muted
              width="200"
              style={{
                position: "absolute",
                bottom: 20,
                right: 20,
                borderRadius: "10px",
                border: "2px solid white",
              }}
            />

            <div
              style={{
                position: "absolute",
                bottom: 20,
                left: 20,
                display: "flex",
                gap: "10px",
              }}
            >
              <Button
                onClick={() => {
                  const videoTrack = localStreamRef.current
                    ?.getTracks()
                    .find((t) => t.kind === "video");
                  if (videoTrack) {
                    videoTrack.enabled = !videoTrack.enabled;
                    setIsVideoOn(videoTrack.enabled);
                  }
                }}
              >
                {isVideoOn ? "Turn Video Off" : "Turn Video On"}
              </Button>
              <Button
                onClick={() => {
                  const audioTrack = localStreamRef.current
                    ?.getTracks()
                    .find((t) => t.kind === "audio");
                  if (audioTrack) {
                    audioTrack.enabled = !audioTrack.enabled;
                    setIsAudioOn(audioTrack.enabled);
                  }
                }}
              >
                {isAudioOn ? "Mute" : "Unmute"}
              </Button>
              <Button danger onClick={handleEndCall}>
                End Call
              </Button>
            </div>
          </div>

          <div
            style={{ flex: 1, display: "flex", padding: "20px", gap: "20px" }}
          >
            <Card
              style={{
                flex: 1,
                borderRadius: "10px",
                boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
                maxHeight: "75vh",
                overflowY: "auto",
              }}
            >
              <div style={{ fontWeight: 500 }}>Patient Notes</div>
              <Divider style={{ margin: 10 }} />

              <Collapse
                accordion
                bordered={false}
                expandIconPosition="end"
                style={{ background: "#fff" }}
              >
                <Panel header="Chief Complaint" key="1">
                  <Input.TextArea
                    rows={3}
                    value={chiefCompliant}
                    onChange={(e) => setChiefCompliant(e.target.value)}
                    placeholder="Enter chief complaint"
                  />
                </Panel>

                <Panel header="Diagnosis" key="2">
                  <Input.TextArea
                    rows={3}
                    value={diagnosis}
                    onChange={(e) => setDiagnosis(e.target.value)}
                    placeholder="Enter diagnosis"
                  />
                </Panel>

                <Panel header="Additional Notes" key="3">
                  <Input.TextArea
                    rows={3}
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Enter notes"
                  />
                </Panel>

                <Panel header="Management Plan" key="4">
                  <Input.TextArea
                    rows={3}
                    value={managementPlan}
                    onChange={(e) => setManagementPlan(e.target.value)}
                    placeholder="Enter management plan"
                  />
                </Panel>

                <Panel header="Allergy" key="5">
                  <Input.TextArea
                    rows={3}
                    value={allergy}
                    onChange={(e) => setAllergy(e.target.value)}
                    placeholder="Enter allergy details"
                  />
                </Panel>

                <Panel header="Medication" key="6">
                  <Input.TextArea
                    rows={3}
                    value={medication}
                    onChange={(e) => setMedication(e.target.value)}
                    placeholder="Enter medications"
                  />
                </Panel>

                <Panel header="Surgery History" key="7">
                  <Input.TextArea
                    rows={3}
                    value={surgeryHistory}
                    onChange={(e) => setSurgeryHistory(e.target.value)}
                    placeholder="Enter surgery history"
                  />
                </Panel>
              </Collapse>

              <Button
                type="primary"
                block
                style={{ marginTop: "20px" }}
                onClick={handleSaveSummary}
              >
                Save & Show Summary
              </Button>
            </Card>
          </div>
        </div>
      )}

      {/* Footer */}
      {/* <div
        style={{
          padding: "10px",
          textAlign: "center",
          background: "#f0f5ff",
          color: "#0050b3",
          fontWeight: 500,
        }}
      >
        🔒 This call is secure. Screenshots are restricted.
      </div> */}

      {/* Summary Modal */}
      <SummaryModal
        isOpen={isSummaryModalOpen}
        onClose={() => setIsSummaryModalOpen(false)}
        patient={{
          id: summary?.patientId,
          name: summary?.patientName,
          age: summary?.patientAge,
          gender: summary?.patientGender,
        }}
        summary={summary}
        doctorName={summary?.doctorName}
      />
    </div>
  );
};

export default DoctorVideoCall;
